import { SatDomain, SatDomainMapEntryDto } from '@/api/sat';

/**
 * The student's personalized course path — the ORDER of the course, derived
 * from their own evidence. This is the product's "we built this FOR you"
 * moment: the sequence isn't our category list, it's their diagnostic.
 *
 * Ordering rule (mirrors the course design + the L5 autopilot's plan):
 *   1. Mastered domains — completed stops, in the order they were mastered.
 *   2. The active domain — "you are here".
 *   3. Remaining domains — weakest first by evidence score (that's the whole
 *      point of the course: attack the weakest area next). Domains without a
 *      score yet sort last, in stable taxonomy order.
 *
 * Keep this in lockstep with the backend autopilot's domain picker when L5
 * lands — the path the student sees must be the plan the system executes.
 */

export type PathNodeStatus = 'DONE' | 'CURRENT' | 'UPCOMING';

export interface CoursePathNode {
  entry: SatDomainMapEntryDto;
  status: PathNodeStatus;
  /** 1-based position on the path. */
  step: number;
}

export function buildCoursePath(
  domains: SatDomainMapEntryDto[],
  activeDomain: SatDomain | null,
): CoursePathNode[] {
  const mastered = domains
    .filter((d) => d.workflowStatus === 'MASTERED')
    .sort((a, b) => (a.masteredAt ?? '').localeCompare(b.masteredAt ?? ''));

  const active = domains.find(
    (d) => d.domain === activeDomain && d.workflowStatus !== 'MASTERED',
  );

  const rest = domains
    .filter((d) => d.workflowStatus !== 'MASTERED' && d.domain !== active?.domain)
    .sort((a, b) => {
      const sa = a.masterySnapshot.overall.scorePct;
      const sb = b.masterySnapshot.overall.scorePct;
      if (sa == null && sb == null) return 0; // stable: keep taxonomy order
      if (sa == null) return 1; // unscored after scored
      if (sb == null) return -1;
      return sa - sb; // weakest first
    });

  const ordered: Array<{ entry: SatDomainMapEntryDto; status: PathNodeStatus }> = [
    ...mastered.map((entry) => ({ entry, status: 'DONE' as const })),
    ...(active ? [{ entry: active, status: 'CURRENT' as const }] : []),
    ...rest.map((entry) => ({ entry, status: 'UPCOMING' as const })),
  ];

  return ordered.map((node, i) => ({ ...node, step: i + 1 }));
}

/** True once the path is personalized — i.e., any evidence-bearing signal exists. */
export function isPathPersonalized(
  domains: SatDomainMapEntryDto[],
  initialDiagnosticCompletedAt: string | null,
): boolean {
  return (
    initialDiagnosticCompletedAt != null ||
    domains.some(
      (d) =>
        d.workflowStatus !== 'NOT_STARTED' ||
        d.masterySnapshot.overall.band !== 'NOT_STARTED',
    )
  );
}
